import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Plus,
  Clock,
  MapPin,
  Users,
  Briefcase,
  Phone,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { calendarService } from '../../services/api';
import CalendarEventForm from './CalendarEventForm';

const CaseCalendarEvents = ({ caseId, onSuccess, onError }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming, past, all
  const [showEventForm, setShowEventForm] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      const params = { case_id: caseId };

      if (filter === 'upcoming') {
        params.start_date = now.toISOString().split('T')[0];
        const future = new Date();
        future.setFullYear(future.getFullYear() + 1);
        params.end_date = future.toISOString().split('T')[0];
      } else if (filter === 'past') {
        const pastStart = new Date();
        pastStart.setFullYear(pastStart.getFullYear() - 2);
        params.start_date = pastStart.toISOString().split('T')[0];
        params.end_date = now.toISOString().split('T')[0];
      } else {
        // all — wide range
        const pastStart = new Date();
        pastStart.setFullYear(pastStart.getFullYear() - 2);
        const future = new Date();
        future.setFullYear(future.getFullYear() + 1);
        params.start_date = pastStart.toISOString().split('T')[0];
        params.end_date = future.toISOString().split('T')[0];
      }

      const response = await calendarService.getEvents(params);
      let fetched = response.data?.events || [];

      // Sort: upcoming = soonest first, past = most recent first
      if (filter === 'past') {
        fetched.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
      } else {
        fetched.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      }

      setEvents(fetched);
    } catch (err) {
      console.error('Error fetching case events:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId, filter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const formatEventType = (type) => {
    switch (type) {
      case 'meeting':
        return { icon: Users, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Meeting' };
      case 'court_date':
        return { icon: Briefcase, color: 'text-red-600', bg: 'bg-red-50', label: 'Court Date' };
      case 'deadline':
        return { icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Deadline' };
      case 'call':
        return { icon: Phone, color: 'text-green-600', bg: 'bg-green-50', label: 'Call' };
      case 'consultation':
        return { icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'Consultation' };
      default:
        return { icon: Calendar, color: 'text-gray-600', bg: 'bg-gray-50', label: type ? type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ') : 'Event' };
    }
  };

  const formatDateTime = (startTime, endTime, allDay) => {
    const start = new Date(startTime);
    const now = new Date();
    const isToday = start.toDateString() === now.toDateString();
    const isTomorrow = start.toDateString() === new Date(now.getTime() + 86400000).toDateString();

    const dateStr = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    if (allDay) return `${dateStr} (All day)`;

    const timeStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    if (endTime) {
      const end = new Date(endTime);
      const endTimeStr = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      return `${dateStr}, ${timeStr} – ${endTimeStr}`;
    }

    return `${dateStr} at ${timeStr}`;
  };

  const isPastEvent = (event) => new Date(event.start_time) < new Date();

  const handleEventClick = (event) => {
    const eventDate = new Date(event.start_time).toISOString().split('T')[0];
    navigate(`/calendar?date=${eventDate}&event=${event.id}`);
  };

  const handleEventCreated = () => {
    setShowEventForm(false);
    fetchEvents();
    if (onSuccess) onSuccess('Event created successfully');
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-xl font-semibold hover:text-gray-600 transition-colors"
          >
            <Calendar className="h-5 w-5" />
            Calendar Events
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setShowEventForm(true)}
            className="bg-yellow-500 text-white px-3 py-1.5 rounded text-sm flex items-center hover:bg-yellow-600 transition-colors"
          >
            <Plus className="mr-1 h-4 w-4" />
            New Event
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-6">
          {/* Filter tabs */}
          <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
            {[
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'past', label: 'Past' },
              { key: 'all', label: 'All' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === key
                    ? 'bg-white text-gray-900 shadow-sm font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Event list */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">
                {filter === 'upcoming' ? 'No upcoming events for this case' :
                 filter === 'past' ? 'No past events for this case' :
                 'No events for this case'}
              </p>
              <button
                onClick={() => setShowEventForm(true)}
                className="mt-2 text-yellow-600 hover:text-yellow-700 text-sm font-medium"
              >
                Schedule an event
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((event) => {
                const eventFormat = formatEventType(event.event_type);
                const Icon = eventFormat.icon;
                const past = isPastEvent(event);

                return (
                  <div
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      past
                        ? 'border-gray-200 bg-gray-50 hover:bg-gray-100 opacity-75'
                        : 'border-gray-200 hover:bg-gray-50 hover:border-yellow-300'
                    }`}
                    title="Click to view in calendar"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-md ${eventFormat.bg} flex-shrink-0 mt-0.5`}>
                        <Icon className={`h-4 w-4 ${eventFormat.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className={`font-medium text-sm truncate ${past ? 'text-gray-500' : 'text-gray-900'}`}>
                            {event.title}
                          </h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${eventFormat.bg} ${eventFormat.color}`}>
                            {eventFormat.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDateTime(event.start_time, event.end_time, event.all_day)}
                        </p>
                        {event.location && (
                          <p className="text-xs text-gray-400 mt-1 flex items-center">
                            <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </p>
                        )}
                        {event.status === 'cancelled' && (
                          <span className="text-xs text-red-500 mt-1 inline-block">Cancelled</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Link to full calendar */}
          {events.length > 0 && (
            <div className="mt-3 text-center">
              <button
                onClick={() => navigate(`/calendar?case=${caseId}`)}
                className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
              >
                View in Calendar
              </button>
            </div>
          )}
        </div>
      )}

      {/* Event Creation Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowEventForm(false)}
              className="absolute top-4 right-4 z-10 p-1 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
            <CalendarEventForm
              initialCaseId={caseId}
              onClose={() => setShowEventForm(false)}
              onEventCreated={handleEventCreated}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseCalendarEvents;
